import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import StockSatelliteHeader from '../features/stock/StockSatelliteHeader';
import StockMagasinPicker from '../features/stock/StockMagasinPicker';
import StockProductReviewModal from '../features/stock/StockProductReviewModal';
import {
  analyzeStockPhoto,
  analyzeStockPhotoFromHint,
  findQueueMatch,
  lineFromAnalyzeResult,
  mergeLineWithExistingStock,
  queueItemToAnalyzeItem,
  queueItemToBulkPayload,
  queueRowFromLine,
  queueRowsMatch,
  recalcVenteFromAchat,
} from '../features/stock/stockAnalyzeApi';
import { analyzeStockLines, registerImeisForArticle } from '../features/stock/stockProductClassify';
import { STOCK_TABS } from './_stockTabs';
import {
  STOCK_PANEL,
  STOCK_BTN_PRIMARY,
  STOCK_BTN_SECONDARY,
  StockFormSection,
} from '../features/stock/stockV2Ui';
import {
  AlertTriangle,
  ArrowLeft,
  Barcode,
  Camera,
  Check,
  ChevronDown,
  ChevronUp,
  DollarSign,
  ImagePlus,
  List,
  Loader2,
  Package,
  Plus,
  Printer,
  RefreshCw,
  Sparkles,
  Store,
  Trash2,
  Truck,
  Warehouse,
} from 'lucide-react';

const WARNING_LABELS = {
  duplicate_reference: 'stockAnalyze.warnDuplicateReference',
  duplicate_ean: 'stockAnalyze.warnDuplicateEan',
  cross_magasin_reference: 'stockAnalyze.warnCrossMagasin',
  low_vision_confidence: 'stockAnalyze.warnLowVision',
  prix_achat_manual: 'stockAnalyze.warnPrixAchatManual',
  llm_vision_unavailable: 'stockAnalyze.warnLlmUnavailable',
};

const INFO_WARNING_CODES = new Set(['duplicate_reference', 'duplicate_ean']);

const StockAnalyzePage = () => {
  const { api, user } = useAuth();
  const { t, tError, formatCurrency } = useLanguage();
  const navigate = useNavigate();
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const [magasinId, setMagasinId] = useState(() => {
    try {
      return localStorage.getItem('pos_current_magasin_id') || user?.default_magasin_id || 'M1';
    } catch {
      return user?.default_magasin_id || 'M1';
    }
  });
  const [magasinsList, setMagasinsList] = useState([]);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [hintText, setHintText] = useState('');
  const [useAiVision, setUseAiVision] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [line, setLine] = useState(null);
  const [pricingDetail, setPricingDetail] = useState(null);
  const [mainOeuvre, setMainOeuvre] = useState(0);
  const [showPricingDetail, setShowPricingDetail] = useState(false);
  const [stockLocation, setStockLocation] = useState('boutique');
  const [recalculating, setRecalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLines, setReviewLines] = useState([]);
  const [reviewAnalyzing, setReviewAnalyzing] = useState(false);
  const [reviewIntent, setReviewIntent] = useState(null);
  const [reviewRowKeys, setReviewRowKeys] = useState([]);
  const [queue, setQueue] = useState([]);
  const [savedArticles, setSavedArticles] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);
  const [queueNotice, setQueueNotice] = useState('');

  useEffect(() => {
    api.get('/magasins?active_only=true')
      .then((r) => setMagasinsList(r.data?.magasins || []))
      .catch(() => {});
  }, [api]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (!line?.reference && !line?.ean) return;
    const candidate = { ...line, stockLocation, magasin_id: magasinId };
    const queueMatch = findQueueMatch(queue, candidate);
    setLine((prev) => {
      if (!prev) return prev;
      const nextInQueue = !!queueMatch;
      if (prev.inQueue === nextInQueue && prev.queueMatchQty === queueMatch?.quantite) return prev;
      return {
        ...prev,
        inQueue: nextInQueue,
        queueMatchQty: queueMatch?.quantite,
      };
    });
  }, [stockLocation, queue, magasinId, line?.reference, line?.ean]);

  const applyResult = useCallback((data) => {
    setResult(data);
    setPricingDetail(data?.pricing?.calcul_detail || null);
    setMainOeuvre(data?.pricing?.main_oeuvre || 0);
  }, []);

  const enrichAnalyzedLine = useCallback(async (data, currentQueue, location) => {
    let pl = lineFromAnalyzeResult(data);
    const ref = pl.reference?.trim();
    const ean = pl.ean?.trim();
    let existing = null;
    if (ref || ean) {
      try {
        const params = new URLSearchParams({ magasin_id: magasinId });
        if (ean) params.set('ean', ean);
        else params.set('ref', ref);
        const r = await api.get(`/stock/by-reference?${params}`);
        if (r.data?.found) existing = r.data.article;
      } catch {
        /* ignore */
      }
    }
    pl = mergeLineWithExistingStock(pl, existing);
    pl.quantite = 1;

    const candidate = { ...pl, stockLocation: location, magasin_id: magasinId };
    const queueMatch = findQueueMatch(currentQueue, candidate);
    if (queueMatch) {
      pl.inQueue = true;
      pl.queueMatchQty = queueMatch.quantite;
    }

    setLine(pl);
  }, [api, magasinId]);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
    setLine(null);
  };

  const handleFileInput = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleAnalyze = async () => {
    if (!file && !hintText.trim()) {
      alert(t('stockAnalyze.alertAddPhotoOrText'));
      return;
    }
    setAnalyzing(true);
    setResult(null);
    try {
      let data;
      if (file) {
        data = await analyzeStockPhoto(api, {
          file,
          magasinId,
          hintText,
          useAiVision,
          quantite: line?.quantite || 1,
        });
      } else {
        data = await analyzeStockPhotoFromHint(api, {
          hintText,
          magasinId,
          useAiVision,
          quantite: line?.quantite || 1,
        });
      }
      applyResult(data);
      await enrichAnalyzedLine(data, queue, stockLocation);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('stockAnalyze.errAnalyzePhoto'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRecalcVente = async () => {
    const pa = parseFloat(line?.prix_achat) || 0;
    if (pa <= 0) {
      alert(t('stockAnalyze.alertEnterPrixAchat'));
      return;
    }
    setRecalculating(true);
    try {
      const detail = await recalcVenteFromAchat(api, {
        prixAchat: pa,
        mainOeuvre,
        reference: line?.reference || '',
      });
      if (detail) {
        setPricingDetail(detail);
        setLine((prev) => ({ ...prev, prix_vente: detail.prix_final }));
      }
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('stockAnalyze.errCalcPrice'));
    } finally {
      setRecalculating(false);
    }
  };

  const applyAlternative = async (alt) => {
    if (!alt) return;
    const pa = parseFloat(alt.price) || 0;
    setLine((prev) => ({
      ...prev,
      designation: alt.part_name || prev?.designation,
      reference: alt.reference || prev?.reference,
      fournisseur: alt.supplier || prev?.fournisseur,
      prix_achat: pa,
    }));
    setResult((prev) => ({
      ...prev,
      supplier_match: alt,
    }));
    if (pa > 0) {
      setRecalculating(true);
      try {
        const detail = await recalcVenteFromAchat(api, {
          prixAchat: pa,
          mainOeuvre,
          reference: alt.reference || '',
        });
        if (detail) {
          setPricingDetail(detail);
          setLine((prev) => ({ ...prev, prix_vente: detail.prix_final }));
        }
      } catch (error) {
        alert(tError(error.response?.data?.detail) || t('stockAnalyze.errCalcPrice'));
      } finally {
        setRecalculating(false);
      }
    }
  };

  const patchLine = (patch) => {
    setLine((prev) => ({ ...prev, ...patch }));
  };

  const resetScan = () => {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setHintText('');
    setResult(null);
    setLine(null);
    setPricingDetail(null);
    setShowPricingDetail(false);
  };

  const commitLineToQueue = () => {
    if (!line?.reference?.trim() || !line?.designation?.trim()) {
      alert(t('stockAnalyze.alertRefDesignationRequired'));
      return;
    }
    if (!parseFloat(line.prix_vente)) {
      alert(t('stockAnalyze.alertPrixVenteRequired'));
      return;
    }
    const addQty = parseInt(line.quantite, 10) || 1;
    const candidate = queueRowFromLine(line, { stockLocation, magasinId, existing: line.existing });

    setQueue((prev) => {
      const matchIdx = prev.findIndex((r) => queueRowsMatch(r, candidate));
      if (matchIdx >= 0) {
        const next = [...prev];
        const row = next[matchIdx];
        const newQty = (row.quantite || 1) + addQty;
        next[matchIdx] = {
          ...row,
          quantite: newQty,
          existing: row.existing || candidate.existing,
          status: row.existing || candidate.existing ? 'existing' : row.status,
          stockArticleId: row.stockArticleId || candidate.stockArticleId,
        };
        setQueueNotice(t('stockAnalyze.noticeAddedToList', { qty: addQty, designation: row.designation, total: newQty }));
        return next;
      }
      const msg = candidate.existing
        ? t('stockAnalyze.noticeStockUpdate', { designation: candidate.designation, qty: addQty })
        : t('stockAnalyze.noticeAdded', { designation: candidate.designation, qty: addQty });
      setQueueNotice(msg);
      return [...prev, candidate];
    });
    resetScan();
    setTimeout(() => setQueueNotice(''), 4500);
  };

  const removeFromQueue = (key) => {
    setQueue((prev) => prev.filter((r) => r.key !== key));
  };

  const executeBulkSave = async (valid, analysisLines = []) => {
    setSaving(true);
    setBulkResult(null);
    const analysisByIndex = Object.fromEntries((analysisLines || []).map((ln, i) => [i, ln]));
    try {
      const res = await api.post('/stock/bulk-add', {
        magasin_id: magasinId,
        items: valid.map((r, idx) => {
          const ln = analysisByIndex[idx] || {};
          const payload = queueItemToBulkPayload(r);
          return {
            ...payload,
            designation: ln.designation || payload.designation,
            categorie: ln.categorie || payload.categorie || ln.suggested_categorie || '',
          };
        }),
      }, { headers: { 'X-Magasin-Session-Id': magasinId } });
      setBulkResult(res.data);
      if (res.data.articles?.length) {
        setSavedArticles(res.data.articles);
        setQueue([]);
        resetScan();
      }
      setReviewOpen(false);

      for (let i = 0; i < valid.length; i += 1) {
        const ln = analysisByIndex[i];
        if (!ln?.needs_imei) continue;
        const imeis = (ln.imei_list || []).filter((x) => String(x || '').replace(/\D/g, '').length >= 14);
        if (imeis.length) {
          await registerImeisForArticle(api, valid[i].reference, imeis, magasinId);
        }
      }
    } catch (error) {
      setBulkResult({ errors: [tError(error.response?.data?.detail) || t('stockAnalyze.errServer')] });
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptLine = async () => {
    if (!line) return;
    if (!line.reference?.trim() || !line.designation?.trim()) {
      alert(t('stockAnalyze.alertRefDesignationRequired'));
      return;
    }
    if (!parseFloat(line.prix_vente)) {
      alert(t('stockAnalyze.alertPrixVenteRequired'));
      return;
    }
    setReviewIntent('accept');
    setReviewAnalyzing(true);
    setReviewOpen(true);
    setReviewRowKeys([]);
    try {
      const data = await analyzeStockLines(api, [{
        reference: line.reference,
        designation: line.designation,
        categorie: line.categorie,
        quantite: line.quantite || 1,
        prix_vente: line.prix_vente,
        prix_achat: line.prix_achat,
      }]);
      setReviewLines(data.lines || []);
      const needsReview = !data.can_save_all || (data.phone_count || 0) > 0 || (data.issue_count || 0) > 0;
      if (!needsReview) {
        setReviewOpen(false);
        commitLineToQueue();
      }
    } catch {
      setReviewOpen(false);
      commitLineToQueue();
    } finally {
      setReviewAnalyzing(false);
    }
  };

  const handleSaveAll = async () => {
    if (!queue.length) return;
    if (!magasinId) {
      alert(t('stockAnalyze.alertChooseMagasin'));
      return;
    }
    setReviewIntent('bulk');
    setReviewAnalyzing(true);
    setReviewOpen(true);
    setReviewRowKeys(queue.map((r) => r.key));
    try {
      const data = await analyzeStockLines(api, queue.map(queueItemToAnalyzeItem));
      setReviewLines(data.lines || []);
      const needsReview = !data.can_save_all || (data.phone_count || 0) > 0 || (data.issue_count || 0) > 0;
      if (!needsReview) {
        setReviewOpen(false);
        await executeBulkSave(queue, data.lines || []);
      }
    } catch (error) {
      setReviewOpen(false);
      setBulkResult({ errors: [tError(error.response?.data?.detail) || t('stockAnalyze.errAnalyzeImpossible')] });
    } finally {
      setReviewAnalyzing(false);
    }
  };

  const handleReviewConfirm = async () => {
    if (reviewIntent === 'accept') {
      const ln = reviewLines[0];
      if (!ln || ln.can_save === false) return;
      if (ln.categorie) patchLine({ categorie: ln.categorie });
      setReviewOpen(false);
      commitLineToQueue();
      return;
    }
    if (reviewIntent === 'bulk') {
      const valid = queue.filter((r) => reviewRowKeys.includes(r.key));
      if (!reviewLines.every((ln) => ln.can_save !== false)) return;
      await executeBulkSave(valid, reviewLines);
    }
  };

  const goToLabels = () => {
    const preselected = savedArticles.map((a) => ({
      id: a.id,
      qty: a.added_qty || 1,
    }));
    navigate('/etiquettes', { state: { preselected, stockFilter: 'boutique' } });
  };

  const resetSummary = () => {
    setSavedArticles([]);
    setBulkResult(null);
  };

  const visionConf = result?.vision?.vision_confidence;
  const match = result?.supplier_match;
  const queueTotalQty = queue.reduce((s, r) => s + (r.quantite || 1), 0);

  if (savedArticles.length > 0) {
    const totalAdded = savedArticles.reduce((s, a) => s + (a.added_qty || 0), 0);
    const createdCount = savedArticles.filter((a) => a.action === 'created').length;
    const updatedCount = savedArticles.filter((a) => a.action === 'updated').length;
    return (
      <Layout>
        <div className="max-w-6xl mx-auto fade-in space-y-4" data-testid="analyze-summary">
          <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
          <StockSatelliteHeader
            title={t('stockAnalyze.summaryTitle')}
            subtitle={t('stockAnalyze.summarySubtitle', { created: createdCount, updated: updatedCount, labels: totalAdded })}
            backTestId="stock-analyze-summary-back"
          />
          <div className="flex flex-wrap gap-2 justify-end">
            <button type="button" onClick={goToLabels} className={STOCK_BTN_PRIMARY} data-testid="analyze-print-labels">
              <Printer className="w-4 h-4" /> {t('stockAnalyze.printLabels', { count: totalAdded })}
            </button>
            <button type="button" onClick={resetSummary} className={STOCK_BTN_SECONDARY} data-testid="analyze-new-session">
              <ArrowLeft className="w-4 h-4" /> {t('stockAnalyze.newSession')}
            </button>
          </div>
          <div className={`${STOCK_PANEL} overflow-x-auto`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b border-white/10">
                  <th className="px-3 py-3 text-left">#</th>
                  <th className="px-3 py-3 text-left">{t('stockAnalyze.colStatut')}</th>
                  <th className="px-3 py-3 text-left">{t('stockAnalyze.colReference')}</th>
                  <th className="px-3 py-3 text-left">{t('stockAnalyze.colDesignation')}</th>
                  <th className="px-3 py-3 text-right">{t('stockAnalyze.colPrixAchat')}</th>
                  <th className="px-3 py-3 text-right">{t('stockAnalyze.colPrixVente')}</th>
                  <th className="px-3 py-3 text-center">{t('stockAnalyze.colQtyAdded')}</th>
                </tr>
              </thead>
              <tbody>
                {savedArticles.map((a, idx) => (
                  <tr key={a.id || idx} className="border-b border-white/5" data-testid={`analyze-summary-row-${idx}`}>
                    <td className="px-3 py-2.5 text-gray-600">{idx + 1}</td>
                    <td className="px-3 py-2.5">
                      {a.action === 'created' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-[10px] font-medium">
                          <Plus className="w-3 h-3" /> {t('stockAnalyze.badgeNew')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 text-[10px] font-medium">
                          <RefreshCw className="w-3 h-3" /> {t('stockAnalyze.badgeAddStock')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-white">{a.reference}</td>
                    <td className="px-3 py-2.5 text-white">{a.designation}</td>
                    <td className="px-3 py-2.5 text-right text-gray-400">{formatCurrency(a.prix_achat || 0)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-400 font-semibold">{formatCurrency(a.prix_vente || 0)}</td>
                    <td className="px-3 py-2.5 text-center text-gray-300">{a.added_qty || 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {bulkResult?.errors?.length > 0 && (
            <div className="p-3 rounded-xl border bg-amber-500/10 border-amber-500/30 text-sm text-amber-200">
              {bulkResult.errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in space-y-4" data-testid="stock-analyze-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <StockSatelliteHeader
          title={t('stockAnalyze.pageTitle')}
          subtitle={t('stockAnalyze.pageSubtitle')}
          backTestId="stock-analyze-back"
        />

        {queueNotice && (
          <div className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-200 flex items-center gap-2" data-testid="analyze-queue-notice">
            <Check className="w-4 h-4 shrink-0" />
            {queueNotice}
          </div>
        )}

        {queue.length > 0 && (
          <div className={`${STOCK_PANEL} p-4 space-y-3`} data-testid="analyze-queue-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-white font-semibold">
                <List className="w-4 h-4 text-red-400" />
                {t('stockAnalyze.pendingList', { count: queue.length, pieces: queueTotalQty })}
              </div>
              <button
                type="button"
                onClick={handleSaveAll}
                disabled={saving || !magasinId}
                className={STOCK_BTN_PRIMARY}
                data-testid="analyze-save-all"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                {t('stockAnalyze.saveAll', { count: queue.length })}
              </button>
            </div>
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {queue.map((row, idx) => (
                <div
                  key={row.key}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg bg-black/30 border border-white/10"
                  data-testid={`analyze-queue-row-${idx}`}
                >
                  <span className="text-xs text-gray-600 w-5">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                      {row.status === 'existing' ? (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-blue-950/40 text-blue-300 border border-blue-500/30 font-bold">
                          {t('stockAnalyze.badgeUpdate')}
                        </span>
                      ) : (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-green-950/40 text-green-300 border border-green-500/30 font-bold">
                          {t('stockAnalyze.badgeNew')}
                        </span>
                      )}
                      <span className="text-[10px] text-amber-300 font-semibold">+{row.quantite}</span>
                    </div>
                    <p className="text-sm text-white truncate">{row.designation}</p>
                    <p className="text-[10px] text-gray-500 font-mono">
                      {row.reference}
                      {row.ean ? ` · EAN ${row.ean}` : ''}
                      {' · '}
                      {row.stockLocation === 'depot' ? t('stockAnalyze.depot') : t('stockAnalyze.boutique')}
                      {row.existing ? ` · ${t('stockAnalyze.stockAbbrev', { boutique: row.existing.quantite || 0, depot: row.existing.quantite_depot || 0 })}` : ''}
                    </p>
                  </div>
                  <span className="text-xs text-emerald-400 font-semibold shrink-0">
                    {formatCurrency(Number(row.prix_vente) || 0)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFromQueue(row.key)}
                    className="p-1.5 text-gray-500 hover:text-red-400"
                    aria-label={t('stockAnalyze.remove')}
                    data-testid={`analyze-queue-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {bulkResult?.errors?.length > 0 && !savedArticles.length && (
          <div className="p-3 rounded-xl border bg-amber-500/10 border-amber-500/30 text-sm text-amber-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>{bulkResult.errors.map((e, i) => <div key={i}>{e}</div>)}</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Colonne photo */}
          <div className="space-y-4">
            <StockFormSection title={t('stockAnalyze.photoSectionTitle')} icon={Camera}>
              <div
                className={`relative rounded-xl border border-dashed border-white/15 bg-black/30 min-h-[220px] flex items-center justify-center overflow-hidden ${
                  previewUrl ? 'p-0' : 'p-6'
                }`}
                data-testid="analyze-photo-preview"
              >
                {previewUrl ? (
                  <img src={previewUrl} alt={t('stockAnalyze.productAlt')} className="w-full h-full max-h-[320px] object-contain" />
                ) : (
                  <div className="text-center text-gray-500 text-sm">
                    <ImagePlus className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    {t('stockAnalyze.photoHint')}
                  </div>
                )}
              </div>
              <input
                ref={galleryRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileInput}
                data-testid="analyze-photo-gallery-input"
              />
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileInput}
                data-testid="analyze-photo-camera-input"
              />
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => galleryRef.current?.click()}
                  className={STOCK_BTN_SECONDARY}
                  data-testid="analyze-photo-gallery"
                >
                  <ImagePlus className="w-4 h-4" /> {t('stockAnalyze.gallery')}
                </button>
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className={STOCK_BTN_SECONDARY}
                  data-testid="analyze-photo-camera"
                >
                  <Camera className="w-4 h-4" /> {t('stockAnalyze.camera')}
                </button>
                <button
                  type="button"
                  onClick={handleAnalyze}
                  disabled={analyzing || (!file && !hintText.trim())}
                  className={STOCK_BTN_PRIMARY}
                  data-testid="analyze-photo-run"
                >
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {analyzing ? t('stockAnalyze.analyzing') : t('stockAnalyze.analyze')}
                </button>
              </div>
              <div className="mt-4">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useAiVision}
                    onChange={(e) => setUseAiVision(e.target.checked)}
                    className="rounded border-white/20"
                    data-testid="analyze-use-ai-vision"
                  />
                  {t('stockAnalyze.aiVisionOption')}
                </label>
              </div>
              <div className="mt-4">
                <label className="block text-xs text-gray-400 mb-1.5">
                  {t('stockAnalyze.labelTextLabel')}
                </label>
                <textarea
                  value={hintText}
                  onChange={(e) => setHintText(e.target.value)}
                  rows={3}
                  className="input-dark w-full text-sm"
                  placeholder={t('stockAnalyze.labelTextPlaceholder')}
                  data-testid="analyze-hint-text"
                />
              </div>
            </StockFormSection>

            <StockMagasinPicker magasinsList={magasinsList} value={magasinId} onChange={setMagasinId} compact />
          </div>

          {/* Colonne résultat */}
          <div className="space-y-4">
            {!line && !analyzing && (
              <div className={`${STOCK_PANEL} p-8 text-center text-gray-500 text-sm`}>
                {t('stockAnalyze.emptyHint')}
              </div>
            )}

            {analyzing && (
              <div className={`${STOCK_PANEL} p-8 flex flex-col items-center gap-3 text-gray-400`}>
                <Loader2 className="w-8 h-8 animate-spin text-red-400" />
                {t('stockAnalyze.analyzingHint')}
              </div>
            )}

            {line && !analyzing && (
              <>
                {(line.existing || line.inQueue) && (
                  <div
                    className="flex flex-col gap-1.5 px-3 py-2.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-xs text-blue-100"
                    data-testid="analyze-duplicate-hint"
                  >
                    {line.existing && (
                      <p className="flex items-center gap-1.5">
                        <RefreshCw className="w-3.5 h-3.5 shrink-0 text-blue-300" />
                        {t('stockAnalyze.alreadyInStockPrefix', { magasin: magasinId })} <strong>+{line.quantite || 1}</strong>
                        {' '}{t('stockAnalyze.alreadyInStockSuffix', { boutique: line.existing.quantite || 0, depot: line.existing.quantite_depot || 0 })}
                      </p>
                    )}
                    {line.inQueue && (
                      <p className="flex items-center gap-1.5">
                        <List className="w-3.5 h-3.5 shrink-0 text-blue-300" />
                        {t('stockAnalyze.alreadyInListPrefix', { qty: line.queueMatchQty })} <strong>+{line.quantite || 1}</strong> {t('stockAnalyze.alreadyInListSuffix')}
                      </p>
                    )}
                  </div>
                )}
                {(result?.warnings || []).map((w, i) => {
                  const isInfo = INFO_WARNING_CODES.has(w.code);
                  return (
                  <div
                    key={`${w.code}-${i}`}
                    className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
                      isInfo
                        ? 'bg-blue-500/10 border border-blue-500/30 text-blue-100'
                        : 'bg-amber-500/10 border border-amber-500/30 text-amber-100'
                    }`}
                    data-testid={`analyze-warning-${w.code}`}
                  >
                    {isInfo ? (
                      <RefreshCw className="w-4 h-4 shrink-0 text-blue-400 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400 mt-0.5" />
                    )}
                    <span>
                      {w.message || (WARNING_LABELS[w.code] ? t(WARNING_LABELS[w.code]) : w.code)}
                      {isInfo && w.existing_qty != null ? ` ${t('stockAnalyze.currentStockBoutique', { qty: w.existing_qty })}` : ''}
                    </span>
                  </div>
                );})}

                <StockFormSection title={t('stockAnalyze.resultTitle')} icon={Package}>
                  {visionConf != null && (
                    <p className="text-[10px] text-purple-400 mb-3">
                      {t('stockAnalyze.readConfidence', { pct: Math.round(visionConf * 100) })}
                      {result?.vision?.vision_source ? ` · ${result.vision.vision_source}` : ''}
                      {result?.ai_vision_used ? ` · ${t('stockAnalyze.aiVision')}` : ''}
                    </p>
                  )}
                  {match && (
                    <p className="text-xs text-emerald-400/90 mb-3 flex items-center gap-1">
                      <Truck className="w-3.5 h-3.5" />
                      {match.supplier} — {t('stockAnalyze.matchLabel')} {match.match_source} ({match.match_score}%)
                    </p>
                  )}

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">{t('stockAnalyze.fieldDesignation')}</label>
                      <input
                        className="input-dark w-full py-2.5"
                        value={line.designation}
                        onChange={(e) => patchLine({ designation: e.target.value })}
                        data-testid="analyze-designation"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">{t('stockAnalyze.fieldReference')}</label>
                        <input
                          className="input-dark w-full py-2.5 font-mono text-sm"
                          value={line.reference}
                          onChange={(e) => patchLine({ reference: e.target.value })}
                          data-testid="analyze-reference"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">EAN</label>
                        <input
                          className="input-dark w-full py-2.5 font-mono text-sm"
                          value={line.ean}
                          onChange={(e) => patchLine({ ean: e.target.value })}
                          data-testid="analyze-ean"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">{t('stockAnalyze.fieldCategorie')}</label>
                        <input
                          className="input-dark w-full py-2.5"
                          value={line.categorie}
                          onChange={(e) => patchLine({ categorie: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">{t('stockAnalyze.fieldFournisseur')}</label>
                        <input
                          className="input-dark w-full py-2.5"
                          value={line.fournisseur}
                          onChange={(e) => patchLine({ fournisseur: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  {(result?.alternatives || []).length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <p className="text-[10px] text-gray-500 uppercase font-bold mb-2">{t('stockAnalyze.otherSupplierParts')}</p>
                      <div className="flex flex-wrap gap-2">
                        {result.alternatives.map((alt, idx) => (
                          <button
                            key={`${alt.reference}-${idx}`}
                            type="button"
                            onClick={() => applyAlternative(alt)}
                            className="text-left px-2 py-1.5 rounded-lg border border-white/10 bg-black/20 hover:border-red-500/30 text-[10px] text-gray-300 max-w-full"
                          >
                            <span className="font-semibold text-white block truncate">{alt.part_name}</span>
                            <span>{formatCurrency(alt.price || 0)} · {alt.supplier}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </StockFormSection>

                <StockFormSection title={t('stockAnalyze.tarifsTitle')} icon={DollarSign}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">{t('stockAnalyze.fieldPrixAchat')}</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="input-dark w-full py-2.5 text-center font-bold"
                          value={line.prix_achat}
                          onChange={(e) => patchLine({ prix_achat: e.target.value })}
                          data-testid="analyze-prix-achat"
                        />
                      </div>
                      {match?.price > 0 && (
                        <p className="text-[10px] text-gray-500 mt-1">{t('stockAnalyze.autoSupplierCache')}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">{t('stockAnalyze.fieldPrixVente')}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="input-dark w-full py-2.5 text-center font-bold text-emerald-300"
                        value={line.prix_vente}
                        onChange={(e) => patchLine({ prix_vente: e.target.value })}
                        data-testid="analyze-prix-vente"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleRecalcVente}
                      disabled={recalculating}
                      className={STOCK_BTN_SECONDARY}
                      data-testid="analyze-recalc-vente"
                    >
                      {recalculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      {t('stockAnalyze.recalcVente')}
                    </button>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{t('stockAnalyze.laborLabel', { amount: formatCurrency(mainOeuvre) })}</span>
                    </div>
                  </div>
                  {pricingDetail && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => setShowPricingDetail((v) => !v)}
                        className="text-xs text-gray-400 flex items-center gap-1 hover:text-white"
                      >
                        {showPricingDetail ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {t('stockAnalyze.detailTitle')}
                      </button>
                      {showPricingDetail && (
                        <div className="mt-2 text-[11px] text-gray-500 space-y-0.5 font-mono bg-black/30 rounded-lg p-3">
                          <div>{t('stockAnalyze.detailAchat')} {formatCurrency(pricingDetail.total_articles ?? pricingDetail.prix_achat_unitaire ?? 0)}</div>
                          <div>{t('stockAnalyze.detailPort')} {formatCurrency(pricingDetail.frais_envoi ?? pricingDetail.frais_port_unitaire ?? 0)}</div>
                          <div>{t('stockAnalyze.detailDouane')} {pricingDetail.frais_douane != null ? formatCurrency(pricingDetail.frais_douane) : '—'}</div>
                          <div>{t('stockAnalyze.detailCoutPiece')} {formatCurrency(pricingDetail.cout_par_piece ?? pricingDetail.cout_douane ?? 0)}</div>
                          <div className="text-emerald-400 font-bold pt-1">
                            {t('stockAnalyze.detailFinal')} {formatCurrency(pricingDetail.prix_final ?? line.prix_vente ?? 0)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </StockFormSection>

                <StockFormSection title={t('stockAnalyze.qtyLocationTitle')} icon={Barcode}>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setStockLocation('boutique')}
                      className={`flex-1 min-w-[100px] py-2 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 ${
                        stockLocation === 'boutique'
                          ? 'border-emerald-500/50 bg-emerald-950/30 text-emerald-300'
                          : 'border-white/10 text-gray-400'
                      }`}
                    >
                      <Store className="w-4 h-4" /> {t('stockAnalyze.boutique')}
                    </button>
                    <button
                      type="button"
                      onClick={() => magasinId === 'M1' && setStockLocation('depot')}
                      disabled={magasinId !== 'M1'}
                      className={`flex-1 min-w-[100px] py-2 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 ${
                        stockLocation === 'depot'
                          ? 'border-blue-500/50 bg-blue-950/30 text-blue-300'
                          : 'border-white/10 text-gray-400'
                      } ${magasinId !== 'M1' ? 'opacity-40' : ''}`}
                    >
                      <Warehouse className="w-4 h-4" /> {t('stockAnalyze.depot')}
                    </button>
                  </div>
                  <label className="block text-xs text-gray-400 mb-1">{t('stockAnalyze.quantite')}</label>
                  <input
                    type="number"
                    min="1"
                    className="input-dark w-full py-2.5 text-center font-bold max-w-[120px]"
                    value={line.quantite}
                    onChange={(e) => patchLine({ quantite: parseInt(e.target.value, 10) || 1 })}
                    data-testid="analyze-quantite"
                  />
                </StockFormSection>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/stock')}
                    className={STOCK_BTN_SECONDARY}
                  >
                    {t('stockAnalyze.quit')}
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptLine}
                    disabled={saving}
                    className={STOCK_BTN_PRIMARY}
                    data-testid="analyze-accept-line"
                  >
                    <Check className="w-4 h-4" />
                    {line.inQueue || line.existing ? t('stockAnalyze.acceptPlusOne') : t('stockAnalyze.acceptLine')}
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  {t('stockAnalyze.acceptHelp')}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <StockProductReviewModal
        open={reviewOpen}
        title={reviewIntent === 'accept' ? t('stockAnalyze.reviewTitleAccept') : t('stockAnalyze.reviewTitleBulk')}
        subtitle={
          reviewIntent === 'accept'
            ? t('stockAnalyze.reviewSubtitleAccept')
            : t('stockAnalyze.reviewBulkSubtitle', { count: queue.length })
        }
        lines={reviewLines}
        analyzing={reviewAnalyzing}
        saving={saving}
        onClose={() => setReviewOpen(false)}
        onConfirm={handleReviewConfirm}
        onLineChange={(index, patch) => {
          setReviewLines((prev) => prev.map((ln) => (ln.index === index ? { ...ln, ...patch } : ln)));
          if (reviewIntent === 'accept') {
            if (patch.categorie) patchLine({ categorie: patch.categorie });
            return;
          }
          const key = reviewRowKeys[index];
          if (!key) return;
          setQueue((prev) => prev.map((r) => {
            if (r.key !== key) return r;
            const next = { ...r };
            if (patch.designation != null) next.designation = patch.designation;
            if (patch.categorie != null) next.categorie = patch.categorie;
            if (patch.imei_list) next.imei_list = patch.imei_list;
            return next;
          }));
        }}
      />
    </Layout>
  );
};

export default StockAnalyzePage;
